// /vendor/$name — vendor profile.
// Until canonicalization (legal_entities) is wired, vendors are virtual —
// keyed by the raw vendor_name string from awards/contracts. The pending
// canonicalization is rendered with the Palantir empty-state pattern.

import { apiFetch } from '@/lib/api';
import { compactUsd, dateOnly, ts } from '@/lib/format';
import { useLocale } from '@/lib/locale';
import { EmptyState, ObjectHeader, TrackButton } from '@/primitives';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_authed/vendor/$name')({
  component: VendorPage,
});

interface VendorDetail {
  id: string | null;
  name: string;
  pending_resolution: boolean;
  legal_name: string | null;
  operating_name: string | null;
  total_spend: { value: number; currency: string } | null;
  contract_count: number;
  award_count: number;
  agency_count: number;
  first_seen: string | null;
  last_seen: string | null;
  top_agencies: Array<{ name: string; spend: { value: number; currency: string }; count: number }>;
}

interface ContractRow {
  id: string;
  publication_date: string;
  jurisdiction: string;
  agency_raw_name: string | null;
  contract_value: number | null;
  total_contract_value: number | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  title: string | null;
}

function VendorPage() {
  const { name } = Route.useParams();
  const { locale } = useLocale();
  const langQs = locale && locale !== 'en' ? `?target_lang=${encodeURIComponent(locale)}` : '';

  const detail = useQuery({
    queryKey: ['vendor', name],
    queryFn: () => apiFetch<VendorDetail>(`/v1/vendors/${encodeURIComponent(name)}`),
  });
  const contracts = useQuery({
    enabled: !!detail.data,
    queryKey: ['vendor', name, 'contracts', locale],
    queryFn: () =>
      apiFetch<{ items: ContractRow[] }>(
        `/v1/vendors/${encodeURIComponent(name)}/contracts${langQs}`,
      ),
  });
  const awards = useQuery({
    enabled: !!detail.data,
    queryKey: ['vendor', name, 'awards', locale],
    queryFn: () =>
      apiFetch<{ items: ContractRow[] }>(`/v1/vendors/${encodeURIComponent(name)}/awards${langQs}`),
  });

  if (detail.isLoading) {
    return (
      <div style={{ flex: 1, position: 'relative' }}>
        <div className="progress" />
        <div className="meta mono" style={{ padding: 24 }}>
          resolving vendor…
        </div>
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <EmptyState
        glyph="VND"
        reason="Vendor not found"
        detail={`No records under "${name}". Try /vendors for the full list.`}
      />
    );
  }

  const v = detail.data;
  const value = v.total_spend?.value ?? 0;

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}
    >
      <ObjectHeader
        type="VND"
        id={v.id?.slice(0, 8) ?? `PEND-${name.slice(0, 6).toUpperCase()}`}
        title={v.legal_name ?? v.name}
        sub={`${v.contract_count} contracts · ${v.award_count} awards · ${v.agency_count} agencies`}
        pills={[
          { tone: 'primary', label: compactUsd(value) },
          ...(v.pending_resolution
            ? [{ tone: 'amber' as const, label: 'PENDING ENTITY RESOLUTION' }]
            : []),
        ]}
        metrics={[
          { k: 'TOTAL SPEND', v: compactUsd(value) },
          { k: 'CONTRACTS', v: ts(v.contract_count) },
          { k: 'AWARDS', v: ts(v.award_count) },
          { k: 'AGENCIES', v: ts(v.agency_count) },
        ]}
        actions={v.id ? <TrackButton entityType="legal_entity" entityId={v.id} /> : undefined}
      />

      {v.pending_resolution && (
        <div
          style={{
            margin: 16,
            padding: '10px 14px',
            border: '1px solid var(--amber-line)',
            background: 'var(--amber-soft)',
            color: 'var(--amber)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ width: 6, height: 6, background: 'var(--amber)' }} />
          this vendor is identified by raw name only · canonicalization to legal_entities lands in
          phase 1
        </div>
      )}

      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '0 16px 16px' }}
      >
        <div className="pane">
          <div className="pane-h">
            <span>TOP AGENCIES (BY SPEND)</span>
          </div>
          <div className="pane-body">
            {v.top_agencies.length === 0 ? (
              <div className="empty">
                <div className="empty-reason">No agency spend recorded</div>
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>AGENCY</th>
                    <th className="r">SPEND</th>
                    <th className="r">COUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {v.top_agencies.map((a) => (
                    <tr key={a.name}>
                      <td>{a.name}</td>
                      <td className="r mn">{compactUsd(a.spend.value)}</td>
                      <td className="r mn">{ts(a.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="pane">
          <div className="pane-h">
            <span>RECENT CONTRACTS</span>
            <span className="meta mono">{contracts.data?.items.length ?? 0}</span>
          </div>
          <div className="pane-body">
            <table className="tbl">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>AGENCY</th>
                  <th className="r">VALUE</th>
                </tr>
              </thead>
              <tbody>
                {(contracts.data?.items ?? []).slice(0, 30).map((c) => (
                  <tr key={c.id}>
                    <td className="mn dim">{dateOnly(c.publication_date)}</td>
                    <td>{truncate(c.agency_raw_name, 28)}</td>
                    <td className="r mn">
                      {compactUsd(c.total_contract_value ?? c.contract_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="pane" style={{ margin: '0 16px 16px' }}>
        <div className="pane-h">
          <span>RECENT AWARDS</span>
          <span className="meta mono">{awards.data?.items.length ?? 0}</span>
        </div>
        <div className="pane-body">
          <table className="tbl">
            <thead>
              <tr>
                <th>DATE</th>
                <th>AGENCY</th>
                <th>TITLE</th>
                <th className="r">VALUE</th>
                <th>JX</th>
              </tr>
            </thead>
            <tbody>
              {(awards.data?.items ?? []).slice(0, 50).map((a) => (
                <tr key={a.id}>
                  <td className="mn dim">{dateOnly(a.publication_date)}</td>
                  <td className="dim">{truncate(a.agency_raw_name, 24)}</td>
                  <td>{truncate(a.title, 60)}</td>
                  <td className="r mn">{compactUsd(a.total_contract_value ?? a.contract_value)}</td>
                  <td className="mn">{a.jurisdiction}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function truncate(s: string | null | undefined, n = 30): string {
  if (!s) return '—';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
